import type { ApprovalInboxEntry } from '@time-tracking/shared';

interface TeamGroupHeaderProps {
  teamId: string;
  teamName: string;
  leadName: string | null;
  entries: ApprovalInboxEntry[];
  stuckCount: number;
  isApproverTeam: boolean;
  isCollapsed: boolean;
  isUnassigned?: boolean;
  /** A closed period owns this week: approving on behalf of the team would be refused. */
  periodClosed?: boolean;
  onToggle: () => void;
  onAobo: () => void;
}

export function TeamGroupHeader({
  teamId,
  teamName,
  leadName,
  entries,
  stuckCount,
  isApproverTeam,
  isCollapsed,
  isUnassigned = false,
  periodClosed = false,
  onToggle,
  onAobo,
}: TeamGroupHeaderProps) {
  const submittedCount = entries.filter((e) => e.status === 'submitted').length;
  const approvedCount = entries.filter((e) => e.status === 'approved').length;
  const returnedCount = entries.filter((e) => e.status === 'returned').length;
  const missingCount = entries.filter((e) => e.status === 'missing' || e.status === 'draft').length;
  const totalCount = entries.length;
  const isComplete = totalCount > 0 && approvedCount + submittedCount === totalCount;
  const leadLabel = leadName ?? 'team';

  return (
    <div className={`team-group-header${isUnassigned ? ' team-group-header--unassigned' : ''}`}>
      <button
        type="button"
        className="team-group-header__toggle"
        aria-expanded={!isCollapsed}
        aria-controls={`team-group-body-${teamId}`}
        aria-label={isCollapsed ? `Expand ${teamName}` : `Collapse ${teamName}`}
        onClick={onToggle}
      >
        {isUnassigned ? (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
            <path d="M8 1L1 14h14L8 1zm0 2.8L13.1 13H2.9L8 3.8zm-.75 3.7v2.5h1.5V7.5h-1.5zm0 3.5v1.5h1.5V11h-1.5z" />
          </svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M7 10l5 5 5-5z" />
          </svg>
        )}
      </button>

      <span className="team-group-header__name">{teamName}</span>

      {isUnassigned ? (
        <span className="unassigned-tag" aria-label="No team assigned">
          no team
        </span>
      ) : (
        <span className="team-group-header__lead">Lead: {leadLabel}</span>
      )}

      <div className="team-group-header__pills">
        {isUnassigned ? (
          <span className="rollup-pill rollup-pill--unassigned">
            {totalCount} {totalCount === 1 ? 'person' : 'people'} unassigned
          </span>
        ) : (
          <>
            {submittedCount > 0 && (
              <span className="rollup-pill rollup-pill--submitted">{submittedCount} submitted</span>
            )}
            {approvedCount > 0 && (
              <span className="rollup-pill rollup-pill--approved">{approvedCount} approved</span>
            )}
            {returnedCount > 0 && (
              <span className="rollup-pill rollup-pill--returned">{returnedCount} returned</span>
            )}
            {missingCount > 0 && (
              <span className="rollup-pill rollup-pill--missing">{missingCount} missing</span>
            )}
            {isApproverTeam && stuckCount > 0 && (
              <span className="rollup-pill rollup-pill--stuck">{stuckCount} stuck</span>
            )}
          </>
        )}
      </div>

      {!isUnassigned && (
        <>
          <div
            className="team-group-header__progress"
            role="img"
            aria-label={`${approvedCount + submittedCount} of ${totalCount} submitted or approved`}
          >
            {isComplete ? (
              <div
                className="team-group-header__progress-fill--approved"
                style={{ width: '100%' }}
              />
            ) : (
              <>
                <div
                  className="team-group-header__progress-fill--approved"
                  style={{ width: `${(approvedCount / totalCount) * 100}%` }}
                />
                <div
                  className="team-group-header__progress-fill--submitted"
                  style={{ width: `${(submittedCount / totalCount) * 100}%` }}
                />
              </>
            )}
          </div>
          <span className="team-group-header__progress-count" aria-hidden="true">
            {approvedCount + submittedCount}/{totalCount}
          </span>
        </>
      )}

      {isUnassigned && (
        <p className="team-group-header__unassigned-meta">
          No approver of record. These people will be missed unless approved manually or assigned to
          a team.
        </p>
      )}

      {!isUnassigned && !isApproverTeam && !periodClosed && submittedCount > 0 && (
        <button type="button" className="aobo-btn aobo-btn--primary" onClick={onAobo}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M12 2L4 6v6c0 5.5 3.8 10.7 8 12 4.2-1.3 8-6.5 8-12V6l-8-4z" />
          </svg>
          Approve {submittedCount} on behalf of {leadLabel}
        </button>
      )}
    </div>
  );
}
